import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import { IROH_SERVICES_API_PREFIX } from "@/api/domain"
import { projectsApi } from "@/api/routes/projects"
import { Tag as SharedTag } from "@/api/tags"
import type { PlanMigrationStatus } from "@/billing/billing"
import type { CloudProvider } from "@/infra"

// ==========================================================================
// Types — Support Tickets
// ==========================================================================

export type SupportTicketStatus =
  | "new"
  | "on_you"
  | "on_customer"
  | "on_hold"
  | "closed"

export type SupportMessageSource = "email" | "discord" | "web" | "system"

export type TicketEventKind =
  | "status_changed"
  | "priority_changed"
  | "assigned"
  | "unassigned"

export interface AssigneeBrief {
  id: string
  username: string
  name: string
  gravatar_hash: string | null
}

export interface TicketSummary {
  id: string
  ticket_number: number
  subject: string
  status: SupportTicketStatus
  priority: number
  customer_email: string
  customer_user_id: string | null
  customer_username: string | null
  assignees: AssigneeBrief[]
  created_at: string
  updated_at: string
  last_customer_reply_at: string | null
  last_agent_reply_at: string | null
}

export interface TicketListResponse {
  tickets: TicketSummary[]
  total: number
  status_counts: Record<string, number>
}

export interface TicketDetail {
  id: string
  ticket_number: number
  subject: string
  status: SupportTicketStatus
  priority: number
  customer_email: string
  customer_user_id: string | null
  customer_username: string | null
  project_id: string | null
  discord_thread_id: number | null
  created_at: string
  updated_at: string
  closed_at: string | null
  last_customer_reply_at: string | null
  last_agent_reply_at: string | null
}

export interface MessageItem {
  id: string
  source: SupportMessageSource
  author_email: string | null
  author_user_id: string | null
  author_name: string | null
  body: string
  internal: boolean
  created_at: string
}

export interface EventItem {
  id: string
  kind: TicketEventKind
  actor_user_id: string | null
  actor_name: string
  old_status: SupportTicketStatus | null
  new_status: SupportTicketStatus | null
  old_priority: number | null
  new_priority: number | null
  target_user_id: string | null
  target_user_name: string | null
  created_at: string
}

export interface AssigneeItem {
  id: string
  username: string
  name: string
  email: string
  gravatar_hash: string | null
  assigned_at: string
}

export interface TicketDetailResponse {
  ticket: TicketDetail
  messages: MessageItem[]
  events: EventItem[]
  assignees: AssigneeItem[]
}

export interface StaffMember {
  id: string
  username: string
  name: string
  email: string
  gravatar_hash: string | null
}

export interface UserSearchItem {
  id: string
  username: string
  name: string
  email: string
}

// ==========================================================================
// Types — Users
// ==========================================================================

export interface StaffUserListItem {
  id: string
  username: string
  email: string
  email_verified: boolean
  created_at: string
}

export interface StaffUserDetail {
  id: string
  username: string
  email: string
  email_verified: boolean
  created_at: string
  discord_user_id: number | null
  is_staff: boolean
}

export interface StaffUserProject {
  id: string
  name: string
  endpoint_id: string | null
  endpoint_enabled: boolean | null
}

// ==========================================================================
// Types — Projects
// ==========================================================================

export interface StaffProjectBase {
  id: string
  owner_id: string
  username: string
  name: string
  billing_plan: string | null
  subscription_status: string
  visibility: string
  created_at: string
}

export interface StaffProjectListItem extends StaffProjectBase {
  collaborator_count: number
  endpoint_count: number
  issues: string[]
  /**
   * Current-month meter readings from Stripe, keyed by meter name (e.g.
   * `RelayHours`). `null` when there's no Stripe subscription or the Stripe
   * call failed; the backend logs the failure so a missing reading is
   * distinguishable from a true zero.
   */
  usage: Record<string, number | null>
}

export interface StaffPlanMigration {
  id: string
  current_plan: string | null
  target_plan: string
  status: PlanMigrationStatus
  created_at: string
  resolved_at: string | null
}

export interface LegacyPriceItem {
  item_id: string
  current_price_id: string
  target_price_id: string
  label: string
}

export interface StaffProjectDetail extends StaffProjectBase {
  owner_id: string
  billing_name: string
  billing_email: string
  stripe_customer_id: string
  stripe_subscription_id: string | null
  description: string
  updated_at: string
  pending_migration: StaffPlanMigration | null
  legacy_price_items: LegacyPriceItem[]
  legacy_price_items_error: string | null
}

export interface UsageSummary {
  meters: Record<string, number>
  period_start: number
  period_end: number
}

export interface StaffProjectStats {
  relay_count: number
  api_key_count: number
  collaborator_count: number
}

export interface StaffCohortMetrics {
  d30_cohort_size: number
  d30_converted_count: number
}

export interface MetricInfo {
  active: boolean
  group: string
  metric: string
  label: string
  description: string
  unit: string
  type: string
}

// ==========================================================================
// Types — Deploys
// ==========================================================================

export interface StaffProjectDeploy {
  id: string
  instance_id: string
  deploy_type: string
  status: string
  url: string | null
  region: string
  orphaned: boolean
  created_at: string
}

// ==========================================================================
// Types — Project Members
// ==========================================================================

export interface StaffProjectMember {
  username: string
  user_id: string
  role: string
  gravatar_hash: string | null
}

interface StaffMembersResponse {
  members: StaffProjectMember[]
  total: number
}

// ==========================================================================
// Types — Upcoming Invoices
// ==========================================================================

export interface UpcomingInvoiceRow {
  project_id: string
  owner_username: string
  project_name: string
  billing_plan: string | null
  subscription_id: string
  /** Unix seconds for the close of the upcoming invoice's period. */
  period_end: number | null
  period_start: number | null
  amount_due_cents: number | null
  currency: string | null
  legacy_items: LegacyPriceItem[]
  /** True when Stripe says no upcoming invoice exists (e.g. trial cancels
   * at end without a payment method). Not a failure — distinct from `error`. */
  no_upcoming_invoice: boolean
  /** Set when Stripe lookup failed for this row. */
  error: string | null
}

export interface UpcomingInvoicesResponse {
  rows: UpcomingInvoiceRow[]
  total: number
}

// ==========================================================================
// Sort enums (must match backend FromFormField values)
// ==========================================================================

export enum SortDir {
  Asc = "asc",
  Desc = "desc",
}

export enum TicketSortColumn {
  TicketNumber = "ticket_number",
  Priority = "priority",
  UpdatedAt = "updated_at",
  CreatedAt = "created_at",
}

export enum UserSortColumn {
  Username = "username",
  Email = "email",
  EmailVerified = "email_verified",
  CreatedAt = "created_at",
}

export enum ProjectSortColumn {
  Name = "name",
  BillingPlan = "billing_plan",
  SubscriptionStatus = "subscription_status",
  CreatedAt = "created_at",
  CollaboratorCount = "collaborator_count",
  EndpointCount = "endpoint_count",
}

// ==========================================================================
// Query params
// ==========================================================================

interface ListTicketsParams {
  status?: SupportTicketStatus
  exclude_status?: SupportTicketStatus
  assignee?: string
  sort_by?: TicketSortColumn
  sort_dir?: SortDir
  offset?: number
  limit?: number
}

// ==========================================================================
// Cache tags
// ==========================================================================

enum Tag {
  Tickets = "StaffTickets",
  TicketDetail = "StaffTicketDetail",
  Staff = "StaffMembers",
  Users = "StaffUsers",
  Projects = "StaffProjects",
  MetricsConfig = "StaffMetricsConfig",
  Entitlements = "StaffEntitlements",
  Deploys = "StaffDeploys",
  ProjectMembers = "StaffProjectMembers",
  PlanMigrations = "StaffPlanMigrations",
  UpcomingInvoices = "StaffUpcomingInvoices",
  CloudProvider = "StaffCloudProvider",
  AllDeploys = "StaffAllDeploys",
}

// ==========================================================================
// Types — Infrastructure
// ==========================================================================

export interface CloudProviderToggle {
  cloud_provider: CloudProvider
}

/** Common fields on every active-workflow variant. */
interface ActiveWorkflowBase {
  workflow_id: string
  /** Action type currently running, e.g. `migrate_instance`. */
  step: string | null
  /** Last log line — current human-readable progress beat. */
  progress: string | null
}

/** Active workflow attached to a deploy row. Discriminated by `verb`;
 * create/migrate carry `target_cloud_provider`, update/delete don't. */
export type ActiveWorkflowInfo =
  | (ActiveWorkflowBase & {
      verb: "create"
      target_cloud_provider: CloudProvider
    })
  | (ActiveWorkflowBase & { verb: "update" })
  | (ActiveWorkflowBase & { verb: "delete" })
  | (ActiveWorkflowBase & {
      verb: "migrate"
      target_cloud_provider: CloudProvider
    })
  | (ActiveWorkflowBase & { verb: "running" })

export interface StaffDeployRow {
  deploy_id: string
  instance_id: string
  project_id: string
  project_name: string
  owner_username: string
  cloud_provider: CloudProvider
  region: string
  deploy_type: string
  status: string
  version: string | null
  url: string
  created_at: string
  active_workflow: ActiveWorkflowInfo | null
}

// ==========================================================================
// API
// ==========================================================================

export const staffApi = createApi({
  reducerPath: "staffApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${IROH_SERVICES_API_PREFIX}staff/`,
    mode: "cors",
    credentials: "include",
  }),
  tagTypes: Object.values(Tag),
  endpoints: (build) => ({
    // ------------------------------------------------------------------
    // Support Tickets
    // ------------------------------------------------------------------
    listTickets: build.query<TicketListResponse, ListTicketsParams>({
      query: ({
        status,
        exclude_status,
        assignee,
        sort_by,
        sort_dir,
        offset = 0,
        limit = 50,
      }) => {
        const params = new URLSearchParams()
        if (status) params.set("status", status)
        if (exclude_status) params.set("exclude_status", exclude_status)
        if (assignee) params.set("assignee", assignee)
        if (sort_by) params.set("sort_by", sort_by)
        if (sort_dir) params.set("sort_dir", sort_dir)
        params.set("offset", String(offset))
        params.set("limit", String(limit))
        return `support-tickets?${params.toString()}`
      },
      providesTags: [Tag.Tickets],
    }),
    getTicket: build.query<TicketDetailResponse, number>({
      query: (ticketNumber) => `support-tickets/${ticketNumber}`,
      providesTags: (_result, _error, ticketNumber) => [
        { type: Tag.TicketDetail, id: ticketNumber },
      ],
    }),
    createTicket: build.mutation<
      { id: string; ticket_number: number; subject: string },
      {
        subject: string
        customer_email: string
        customer_user_id?: string
        priority?: number
        message?: string
        assignee_ids?: string[]
      }
    >({
      query: (body) => ({
        url: "support-tickets",
        method: "POST",
        body,
      }),
      invalidatesTags: [Tag.Tickets],
    }),
    updateTicketStatus: build.mutation<
      void,
      { ticketNumber: number; status: SupportTicketStatus }
    >({
      query: ({ ticketNumber, status }) => ({
        url: `support-tickets/${ticketNumber}/status`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: (_r, _e, { ticketNumber }) => [
        Tag.Tickets,
        { type: Tag.TicketDetail, id: ticketNumber },
      ],
    }),
    updateTicketPriority: build.mutation<
      void,
      { ticketNumber: number; priority: number }
    >({
      query: ({ ticketNumber, priority }) => ({
        url: `support-tickets/${ticketNumber}/priority`,
        method: "PUT",
        body: { priority },
      }),
      invalidatesTags: (_r, _e, { ticketNumber }) => [
        Tag.Tickets,
        { type: Tag.TicketDetail, id: ticketNumber },
      ],
    }),
    updateTicketAssignees: build.mutation<
      void,
      { ticketNumber: number; user_ids: string[] }
    >({
      query: ({ ticketNumber, user_ids }) => ({
        url: `support-tickets/${ticketNumber}/assignees`,
        method: "PUT",
        body: { user_ids },
      }),
      invalidatesTags: (_r, _e, { ticketNumber }) => [
        Tag.Tickets,
        { type: Tag.TicketDetail, id: ticketNumber },
      ],
    }),
    updateTicketCustomer: build.mutation<
      void,
      {
        ticketNumber: number
        customer_user_id: string | null
        customer_email?: string | null
        project_id?: string | null
      }
    >({
      query: ({ ticketNumber, ...body }) => ({
        url: `support-tickets/${ticketNumber}/customer`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { ticketNumber }) => [
        Tag.Tickets,
        { type: Tag.TicketDetail, id: ticketNumber },
      ],
    }),
    addTicketMessage: build.mutation<
      MessageItem,
      { ticketNumber: number; body: string; internal: boolean }
    >({
      query: ({ ticketNumber, body, internal }) => ({
        url: `support-tickets/${ticketNumber}/messages`,
        method: "POST",
        body: { body, internal },
      }),
      invalidatesTags: (_r, _e, { ticketNumber }) => [
        Tag.Tickets,
        { type: Tag.TicketDetail, id: ticketNumber },
      ],
    }),
    listStaff: build.query<StaffMember[], void>({
      query: () => "staff",
      providesTags: [Tag.Staff],
    }),
    searchUsers: build.query<UserSearchItem[], string>({
      query: (q) => `users/search?q=${encodeURIComponent(q)}`,
    }),

    // ------------------------------------------------------------------
    // Users
    // ------------------------------------------------------------------
    listUsers: build.query<
      StaffUserListItem[],
      {
        offset?: number
        limit?: number
        q?: string
        sort_by?: UserSortColumn
        sort_dir?: SortDir
      }
    >({
      query: ({ offset = 0, limit = 50, q, sort_by, sort_dir }) => {
        const params = new URLSearchParams()
        params.set("offset", String(offset))
        params.set("limit", String(limit))
        if (q) params.set("q", q)
        if (sort_by) params.set("sort_by", sort_by)
        if (sort_dir) params.set("sort_dir", sort_dir)
        return `users?${params.toString()}`
      },
      providesTags: [Tag.Users],
    }),
    getUser: build.query<StaffUserDetail, string>({
      query: (userId) => `users/${userId}`,
    }),
    getUserProjects: build.query<StaffUserProject[], string>({
      query: (userId) => `users/${userId}/projects`,
    }),
    // ------------------------------------------------------------------
    // Projects
    // ------------------------------------------------------------------
    listProjects: build.query<
      StaffProjectListItem[],
      {
        offset?: number
        limit?: number
        billing_issue?: string
        q?: string
        sort_by?: ProjectSortColumn
        sort_dir?: SortDir
      }
    >({
      query: ({
        offset = 0,
        limit = 50,
        billing_issue,
        q,
        sort_by,
        sort_dir,
      }) => {
        const params = new URLSearchParams()
        params.set("offset", String(offset))
        params.set("limit", String(limit))
        if (billing_issue) params.set("billing_issue", billing_issue)
        if (q) params.set("q", q)
        if (sort_by) params.set("sort_by", sort_by)
        if (sort_dir) params.set("sort_dir", sort_dir)
        return `projects?${params.toString()}`
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((p) => ({ type: Tag.Projects as const, id: p.id })),
              { type: Tag.Projects, id: "LIST" },
            ]
          : [{ type: Tag.Projects, id: "LIST" }],
    }),
    getProject: build.query<StaffProjectDetail, string>({
      query: (projectId) => `projects/${projectId}`,
      providesTags: (_result, _error, projectId) => [
        { type: Tag.Projects, id: projectId },
      ],
    }),
    getProjectStats: build.query<StaffProjectStats, string>({
      query: (projectId) => `projects/${projectId}/stats`,
      providesTags: (_result, _error, projectId) => [
        { type: Tag.Projects, id: projectId },
      ],
    }),
    getCohortMetrics: build.query<StaffCohortMetrics, void>({
      query: () => "metrics/cohort",
    }),
    getProjectUsage: build.query<
      UsageSummary,
      { projectId: string; start: number; end: number }
    >({
      query: ({ projectId, start, end }) =>
        `projects/${projectId}/usage?start=${start}&end=${end}`,
    }),
    toggleEndpoint: build.mutation<
      void,
      { projectId: string; isEnabled: boolean }
    >({
      query: ({ projectId, isEnabled }) => ({
        url: `projects/${projectId}/endpoint`,
        method: "PATCH",
        body: { is_enabled: isEnabled },
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: Tag.Projects, id: projectId },
        { type: Tag.Projects, id: "LIST" },
      ],
    }),
    getProjectMetricsConfig: build.query<MetricInfo[], string>({
      query: (projectId) => `projects/${projectId}/metrics_config`,
      providesTags: (_result, _error, projectId) => [
        { type: Tag.MetricsConfig, id: projectId },
      ],
    }),
    putProjectMetricsConfig: build.mutation<
      void,
      { projectId: string; config: MetricInfo[] }
    >({
      query: ({ projectId, config }) => ({
        url: `projects/${projectId}/metrics_config`,
        method: "PUT",
        body: config,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: Tag.MetricsConfig, id: projectId },
      ],
    }),
    getProjectEntitlements: build.query<string[], string>({
      query: (projectId) => `projects/${projectId}/entitlements`,
      providesTags: (_result, _error, projectId) => [
        { type: Tag.Entitlements, id: projectId },
      ],
    }),
    putProjectEntitlements: build.mutation<
      void,
      { projectId: string; entitlements: string[] }
    >({
      query: ({ projectId, entitlements }) => ({
        url: `projects/${projectId}/entitlements`,
        method: "PUT",
        body: entitlements,
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: Tag.Entitlements, id: projectId },
      ],
    }),

    // ------------------------------------------------------------------
    // Upcoming Invoices (Stripe fan-out, paginated)
    // ------------------------------------------------------------------
    listUpcomingInvoices: build.query<
      UpcomingInvoicesResponse,
      { offset?: number; limit?: number }
    >({
      query: ({ offset = 0, limit = 25 }) => {
        const params = new URLSearchParams()
        params.set("offset", String(offset))
        params.set("limit", String(limit))
        return `upcoming-invoices?${params.toString()}`
      },
      providesTags: [Tag.UpcomingInvoices],
    }),
    migrateProjectPrices: build.mutation<{ migrated: number }, string>({
      query: (projectId) => ({
        url: `projects/${projectId}/migrate-prices`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, projectId) => [
        Tag.UpcomingInvoices,
        { type: Tag.Projects, id: projectId },
      ],
    }),

    // ------------------------------------------------------------------
    // Plan Migrations
    // ------------------------------------------------------------------
    createPlanMigration: build.mutation<
      StaffPlanMigration,
      { projectId: string; target_plan: string }
    >({
      query: ({ projectId, target_plan }) => ({
        url: `projects/${projectId}/plan-migration`,
        method: "POST",
        body: { target_plan },
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: Tag.Projects, id: projectId },
        { type: Tag.PlanMigrations, id: projectId },
        { type: Tag.Projects, id: "LIST" },
      ],
    }),
    getPlanMigration: build.query<StaffPlanMigration, string>({
      query: (projectId) => `projects/${projectId}/plan-migration`,
      providesTags: (_result, _error, projectId) => [
        { type: Tag.PlanMigrations, id: projectId },
      ],
    }),
    cancelPlanMigration: build.mutation<void, string>({
      query: (projectId) => ({
        url: `projects/${projectId}/plan-migration`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, projectId) => [
        { type: Tag.Projects, id: projectId },
        { type: Tag.PlanMigrations, id: projectId },
        { type: Tag.Projects, id: "LIST" },
      ],
    }),

    // ------------------------------------------------------------------
    // Deploys
    // ------------------------------------------------------------------
    listProjectDeploys: build.query<
      StaffProjectDeploy[],
      { projectId: string; offset?: number; limit?: number }
    >({
      query: ({ projectId, offset = 0, limit = 50 }) =>
        `projects/${projectId}/deploys?offset=${offset}&limit=${limit}`,
      providesTags: (_result, _error, { projectId }) => [
        { type: Tag.Deploys, id: projectId },
      ],
    }),

    deleteProjectDeploy: build.mutation<
      void,
      { projectId: string; deployId: string }
    >({
      query: ({ projectId, deployId }) => ({
        url: `projects/${projectId}/deploys/${deployId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: Tag.Deploys, id: projectId },
      ],
    }),

    // ------------------------------------------------------------------
    // Project Members
    // ------------------------------------------------------------------
    getProjectMembers: build.query<
      StaffMembersResponse,
      { projectId: string; offset?: number; limit?: number }
    >({
      query: ({ projectId, offset = 0, limit = 50 }) =>
        `projects/${projectId}/members?offset=${offset}&limit=${limit}`,
      providesTags: (_result, _error, { projectId }) => [
        { type: Tag.ProjectMembers, id: projectId },
      ],
    }),
    addProjectMember: build.mutation<
      void,
      { projectId: string; username: string; role: string }
    >({
      query: ({ projectId, username, role }) => ({
        url: `projects/${projectId}/members`,
        method: "POST",
        body: { username, role },
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: Tag.ProjectMembers, id: projectId },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(projectsApi.util.invalidateTags([SharedTag.MyProjects]))
      },
    }),
    removeProjectMember: build.mutation<
      void,
      { projectId: string; userId: string }
    >({
      query: ({ projectId, userId }) => ({
        url: `projects/${projectId}/members/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: Tag.ProjectMembers, id: projectId },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(projectsApi.util.invalidateTags([SharedTag.MyProjects]))
      },
    }),
    transferProjectOwner: build.mutation<
      void,
      { projectId: string; newOwnerId: string }
    >({
      query: ({ projectId, newOwnerId }) => ({
        url: `projects/${projectId}/transfer-owner`,
        method: "POST",
        body: { new_owner_id: newOwnerId },
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: Tag.Projects, id: projectId },
        { type: Tag.ProjectMembers, id: projectId },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(projectsApi.util.invalidateTags([SharedTag.MyProjects]))
      },
    }),

    // ------------------------------------------------------------------
    // Infrastructure
    // ------------------------------------------------------------------
    getCloudProvider: build.query<CloudProviderToggle, void>({
      query: () => "cloud-provider",
      providesTags: [Tag.CloudProvider],
    }),
    setCloudProvider: build.mutation<CloudProviderToggle, CloudProvider>({
      query: (cloud_provider) => ({
        url: "cloud-provider",
        method: "POST",
        body: { cloud_provider },
      }),
      invalidatesTags: [Tag.CloudProvider],
    }),
    listAllDeploys: build.query<StaffDeployRow[], void>({
      query: () => "deploys",
      providesTags: [Tag.AllDeploys],
    }),
    migrateDeploy: build.mutation<
      unknown,
      { deployId: string; target: CloudProvider }
    >({
      query: ({ deployId, target }) => ({
        url: `deploys/${deployId}/migrate`,
        method: "POST",
        body: { target_cloud_provider: target },
      }),
      invalidatesTags: [Tag.AllDeploys],
    }),
    forceUpdateDeploy: build.mutation<
      unknown,
      { deployId: string; buildId?: string }
    >({
      query: ({ deployId, buildId }) => ({
        url: `deploys/${deployId}/force-update`,
        method: "POST",
        body: { build_id: buildId },
      }),
      invalidatesTags: [Tag.AllDeploys],
    }),
  }),
})

export const {
  // Support Tickets
  useListTicketsQuery,
  useGetTicketQuery,
  useCreateTicketMutation,
  useUpdateTicketStatusMutation,
  useUpdateTicketPriorityMutation,
  useUpdateTicketAssigneesMutation,
  useUpdateTicketCustomerMutation,
  useAddTicketMessageMutation,
  useListStaffQuery,
  useSearchUsersQuery,
  // Users
  useListUsersQuery,
  useGetUserQuery,
  useGetUserProjectsQuery,
  // Projects
  useListProjectsQuery,
  useGetProjectQuery,
  useGetProjectStatsQuery,
  useGetCohortMetricsQuery,
  useGetProjectUsageQuery,
  useToggleEndpointMutation,
  useGetProjectMetricsConfigQuery,
  usePutProjectMetricsConfigMutation,
  useGetProjectEntitlementsQuery,
  usePutProjectEntitlementsMutation,
  // Plan Migrations
  useCreatePlanMigrationMutation,
  useGetPlanMigrationQuery,
  useCancelPlanMigrationMutation,
  // Upcoming Invoices / Price Migrations
  useListUpcomingInvoicesQuery,
  useMigrateProjectPricesMutation,
  // Deploys
  useListProjectDeploysQuery,
  useDeleteProjectDeployMutation,
  // Project Members
  useGetProjectMembersQuery,
  useAddProjectMemberMutation,
  useRemoveProjectMemberMutation,
  useTransferProjectOwnerMutation,
  // Infrastructure
  useGetCloudProviderQuery,
  useSetCloudProviderMutation,
  useListAllDeploysQuery,
  useMigrateDeployMutation,
  useForceUpdateDeployMutation,
} = staffApi
